import React, { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Checkbox, FormGroup, Input } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormDialog } from "components/dialogs/Dialogs";
import CurrencySelect from "components/selects/CurrencySelect";
import NumberInput from "components/cck/NumberInput";
import OverflowTooltip from "components/cck/OverflowTooltip";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import CostTypeSelect from "view/costtypes/components/selects/CostTypeSelect";
import VatCodeSelect from "view/vatcodes/components/selects/VatCodeSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import { normalizeUnit } from "view/declarations/Declarations";

import { DataFormat } from "imports/Utils";

import { invoiceLineSchema, InvoiceLineSchemaInput, InvoiceLineSchemaOutput } from "view/invoicing/utils/invoicing.schema";

import { ChildPropsFromPagination } from "types/commons";

import { InsertOrUpdateInvoiceLineDocument } from "__generated__/view/invoicing/graphql/invoicing.mutations.generated";
import { SearchInvoiceLinesQuery } from "__generated__/view/invoicing/graphql/invoicing.queries.generated";

type InvoiceLineFromQuery = ChildPropsFromPagination<SearchInvoiceLinesQuery, "searchInvoicelines">

interface InvoiceLineEditDialogProps {
	open: boolean;
	onClose: () => void;
	invoiceLine: InvoiceLineFromQuery | null;
	refetch: () => void;
	charter?: {
		id: number;
		creditor: RelationSelectOption;
	}
	declarationId?: number;
}

const InvoiceLineEditDialog = ({ open, onClose, invoiceLine, refetch, charter, declarationId }: InvoiceLineEditDialogProps) => {

	const isEditing = !!invoiceLine?.id;

	const { currencies } = useStoreState(store => store.session);
	const defaultCurrency = currencies?.find(currency => currency.isoCode === "EUR");

	const hasAgreement = !!invoiceLine?.agreement;

	const defaultValues = {
		id: null,
		price: {
			currency: defaultCurrency ?? null,
			value: null,
		},
		quantity: 1,
		costType: null,
		vatCode: null,
		description: "",
		unexpected: false,
		invoiceReference: "",
		creditor: charter?.creditor ?? null,
	};

	const form = useForm<InvoiceLineSchemaInput, unknown, InvoiceLineSchemaOutput>({
		resolver: zodResolver(invoiceLineSchema(hasAgreement)),
		defaultValues: defaultValues,
	});

	const { control, handleSubmit, reset } = form;

	const values = useWatch({ control });

	const [ saveInvoiceLine, { loading } ] = useMutation(InsertOrUpdateInvoiceLineDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: InvoiceLineSchemaOutput) => {

		saveInvoiceLine({
			variables: {
				input: {
					id: data.id ?? undefined,
					description: data.description,
					quantity: data.quantity,
					price: data.price ? {
						value: data.price.value ?? 0,
						currencyIso: data.price.currency?.isoCode ?? "EUR",
					} : undefined,
					vatCodeId: data.vatCode.id,
					costTypeId: data.costType?.id ?? undefined,
					creditor: data.creditor ? { id: data.creditor.id, name: data.creditor.name } : undefined,
					unexpected: data.unexpected,
					invoiceReference: data.invoiceReference,
					charterId: isEditing ? undefined : charter?.id,
					declarationId: isEditing ? undefined : declarationId,
				},
			},
		}).then(() => {
			handleOnClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open && invoiceLine) {
			reset({
				id: invoiceLine.id,
				price: {
					currency: invoiceLine.price?.currency ?? null,
					value: invoiceLine.price?.value ?? null,
				},
				quantity: invoiceLine.quantity ?? null,
				costType: invoiceLine.costType ?? null,
				vatCode: invoiceLine.vatCode ?? null,
				description: invoiceLine.description ?? "",
				unexpected: invoiceLine.unexpected ?? false,
				invoiceReference: invoiceLine.invoiceReference ?? "",
				creditor: invoiceLine.creditor ?? null,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: isEditing ? "INVOICE_LINE" : "ADD_INVOICE_LINE" })}
			form="invoiceLineEditForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="invoiceLineEditForm">
				<Grid container spacing={2}>
					{hasAgreement && (
						<Grid size={12}>
							<FormGroup label={intl.formatMessage({ id: `AGREEMENT_TYPE_${invoiceLine?.agreement?.type}` })}>
								<OverflowTooltip>
									<span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
										{invoiceLine?.agreement?.description && <>{invoiceLine.agreement.description}: </>}
										{invoiceLine?.agreement?.calculationMethod?.summary}
									</span>
								</OverflowTooltip>
							</FormGroup>
						</Grid>
					)}

					<Grid size={3.5}>
						<FormGroup label={intl.formatMessage({ id: "INVOICE_PRICE" })} overflow="hidden" required>
							<Controller
								name="price.value"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput {...field} addonBefore={values.price?.currency?.symbol} decimalScale={2} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Grid>

					<Grid
						size={1}
						display="flex"
						alignItems="center"
						justifyContent="center"
						sx={{ pt: 2 }}
					>
						×
					</Grid>

					<Grid size={3.5}>
						<FormGroup label={intl.formatMessage({ id: "INVOICE_QUANTITY" })} overflow="hidden" required>
							<Controller
								name="quantity"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput {...field} addonAfter={normalizeUnit(invoiceLine?.unit)} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Grid>

					<Grid
						size={1}
						display="flex"
						alignItems="center"
						justifyContent="center"
						sx={{ pt: 2 }}>
						=
					</Grid>

					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "TOTAL_EXCLUDE_VAT" })} overflow="hidden">
							<div style={{ display: "flex", alignItems: "center", height: "32px" }}>
								{DataFormat.price((values.price?.value || 0) * (values.quantity || 0), values.price?.currency?.isoCode as string)}
							</div>
						</FormGroup>
					</Grid>

					{!hasAgreement && (
						<Grid size={3}>
							<FormGroup label={intl.formatMessage({ id: "CONTRACT_CURRENCY" })} overflow="hidden" required>
								<Controller
									name="price.currency"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<CurrencySelect {...field} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</Grid>
					)}

					<Grid size={hasAgreement ? 4.5 : 3}>
						<FormGroup label={intl.formatMessage({ id: "COST_TYPE" })} overflow="hidden" required={!hasAgreement}>
							<Controller
								name="costType"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<CostTypeSelect {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Grid>

					<Grid size={hasAgreement ? 4.5 : 3}>
						<FormGroup label={intl.formatMessage({ id: "INVOICE_VAT" })} overflow="hidden" required>
							<Controller
								name="vatCode"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<VatCodeSelect {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "TOTAL_INCLUDE_VAT" })} overflow="hidden">
							<div style={{ display: "flex", alignItems: "center", height: "32px" }}>
								{DataFormat.price((values.price?.value || 0) * (values.quantity || 0) * (1 + (values.vatCode?.percentage || 0) / 100), values.price?.currency?.isoCode as string)}
							</div>
						</FormGroup>
					</Grid>

					<Grid size={9}>
						<FormGroup label={intl.formatMessage({ id: "DESCRIPTION" })} overflow="hidden">
							<Controller
								name="description"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "UNEXPECTED" })} overflow="hidden">
							<Controller
								name="unexpected"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
						</FormGroup>
					</Grid>

					<Grid size={6}>
						<FormGroup label={intl.formatMessage({ id: "INVOICE_REFERENCE" })} overflow="hidden">
							<Controller
								name="invoiceReference"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Grid>

					<Grid size={6}>
						<FormGroup label={intl.formatMessage({ id: "CREDITOR" })} overflow="hidden">
							<Controller
								name="creditor"
								control={control}
								render={({ field }) => <RelationSelect {...field} />} />
						</FormGroup>
					</Grid>
				</Grid>
			</form>
		</FormDialog>
	);
};

export default InvoiceLineEditDialog;
